import 'moment-timezone'

import { CampaignPostDTO, SocialCampaignDTO } from 'models/socialCampaign'
import { PostDisplayStatus, dayLocaleOf, postDisplayStatus } from './campaignStatus'
import { addDays, mondayOf, postSlotOf } from './schedule'

import moment from 'moment'

/**
 * The schedule's week calendar (ISSUE-3649, prototype v3 `calendarHtml` / `syncCalendar`, v1's `renderWeek` /
 * `renderDay`): an hour grid from 6:00 to 21:00 over the seven days of a week, in the campaign's timezone; on a phone
 * the picked day hour by hour.
 */

export const CALENDAR_FIRST_HOUR = 6
export const CALENDAR_LAST_HOUR = 21
export const CALENDAR_HOURS = Array.from({ length: CALENDAR_LAST_HOUR - CALENDAR_FIRST_HOUR + 1 }, (_, index) => CALENDAR_FIRST_HOUR + index)
/** Under it the week's seven columns no longer read: the day agenda (prototype v3, tablet portrait included) */
export const CALENDAR_DAY_QUERY = '(max-width: 860px)'
/** The hour the grid or the agenda falls back on when nothing says where to open */
const DEFAULT_HOUR = 9

const clampHour = (hour: number) => Math.min(CALENDAR_LAST_HOUR, Math.max(CALENDAR_FIRST_HOUR, hour))

/** The seven days of a week, Monday first (YYYY-MM-DD). */
export const weekDaysOf = (monday: string) => Array.from({ length: 7 }, (_, index) => addDays(monday, index))

/** Today and the hour in progress, in the campaign's timezone. */
export interface CalendarClock {
  today: string
  hour: number
  /** Now (YYYY-MM-DDTHH:mm) */
  now: string
  /** The earliest a new post may go out: now plus the edit freeze (YYYY-MM-DDTHH:mm) */
  earliest: string
}

export const calendarClockOf = (now: Date, timezone: string, editFreezeMinutes = 0): CalendarClock => {
  const at = moment(now).tz(timezone)
  return {
    today: at.format('YYYY-MM-DD'),
    hour: at.hour(),
    now: at.format('YYYY-MM-DDTHH:mm'),
    earliest: at.clone().add(editFreezeMinutes, 'minutes').format('YYYY-MM-DDTHH:mm'),
  }
}

/** A post in the calendar: the day and time it goes out in the campaign's timezone, and the row it sits in. */
export interface CalendarPlacement {
  post: CampaignPostDTO
  day: string
  /** HH:mm */
  time: string
  /** Its row: a post before 6:00 or after 21:59 sits in the first or the last one (prototype `hourOf`) */
  hour: number
  /** Before 6:00 or after 21:59: it sits in that row without taking its hour */
  outsideGrid: boolean
}

/** Every post where the calendar puts it, in publication order. */
export const placePosts = (posts: CampaignPostDTO[], timezone: string): CalendarPlacement[] => posts
  .map((post) => {
    const slot = postSlotOf(post, timezone)
    const hour = Number(slot.time.slice(0, 2))
    return { post, day: slot.day, time: slot.time, hour: clampHour(hour), outsideGrid: clampHour(hour) !== hour }
  })
  .sort((first, second) => Date.parse(first.post.publishAt) - Date.parse(second.post.publishAt))

export const cellKeyOf = (day: string, hour: number) => `${day}@${hour}`

/** The posts of each hour of each day: two posts in one hour share its cell. */
export const postsByCell = (placements: CalendarPlacement[]) => {
  const cells = new Map<string, CalendarPlacement[]>()
  for (const placement of placements) {
    const key = cellKeyOf(placement.day, placement.hour)
    cells.set(key, [...(cells.get(key) ?? []), placement])
  }
  return cells
}

/**
 * An hour takes no new post (greyed) once its HH:00 is not after now plus the edit freeze: the editor would refuse it
 * as too soon (`postBlockerOf`). So the hour in progress has gone, and the next one in the freeze's last minutes.
 */
export const isHourGone = (day: string, hour: number, clock: CalendarClock) => `${day}T${calendarSlotTime(hour)}` <= clock.earliest

/** Whether a cell shows as gone: its hour has gone and nothing in it is still ahead (a post after 21:59 sits there). */
export const isCellGone = (day: string, hour: number, here: CalendarPlacement[], clock: CalendarClock) =>
  isHourGone(day, hour, clock) && here.every((placement) => `${placement.day}T${placement.time}` <= clock.now)

/** A cell offers its hour when that hour has not gone and no post of that very hour is in it. */
export const isCellFree = (day: string, hour: number, here: CalendarPlacement[], clock: CalendarClock) =>
  !isHourGone(day, hour, clock) && here.every((placement) => placement.outsideGrid)

/** The posts a week cell shows: two side by side, the others behind a "+N" */
export const CELL_SHOWN_POSTS = 2

const isGone = (status: PostDisplayStatus) => status === PostDisplayStatus.PUBLISHED || status === PostDisplayStatus.NOT_PUBLISHED

/** The calendar opens on the week of the first post still to go out, else on this week. */
export const initialCalendarWeek = (campaign: SocialCampaignDTO, now: Date) => {
  const next = placePosts(campaign.posts, campaign.timezone).find(({ post }) => !isGone(postDisplayStatus(post, campaign, now)))
  return mondayOf(next?.day ?? calendarClockOf(now, campaign.timezone).today)
}

/** The day a phone shows in a week: today in this week, else the week's first post's day, else its Monday. */
export const initialCalendarDay = (monday: string, placements: CalendarPlacement[], clock: CalendarClock) => {
  if (mondayOf(clock.today) === monday) return clock.today
  return placements.find((placement) => mondayOf(placement.day) === monday)?.day ?? monday
}

/** The row the view scrolls to: the hour before the one it opens on, so that one shows under it. */
const rowAbove = (hour: number) => clampHour(hour - 1)

/** The grid opens on the week's first post's hour; a week without a post on the hour in progress, else 9:00. */
export const gridOpeningRow = (days: string[], placements: CalendarPlacement[], clock: CalendarClock) => {
  const hours = placements.filter((placement) => days.includes(placement.day)).map((placement) => placement.hour)
  if (hours.length) return rowAbove(Math.min(...hours))
  return rowAbove(days.includes(clock.today) ? clock.hour : DEFAULT_HOUR)
}

/** The agenda opens today on the hour in progress, another day on its first post, else on 9:00. */
export const agendaOpeningRow = (day: string, placements: CalendarPlacement[], clock: CalendarClock) => {
  if (day === clock.today) return rowAbove(clock.hour)
  const hours = placements.filter((placement) => placement.day === day).map((placement) => placement.hour)
  return rowAbove(hours.length ? Math.min(...hours) : DEFAULT_HOUR)
}

/** A row of the grid or the agenda ("6:00", "15:00"). */
export const formatCalendarHour = (hour: number) => `${hour}:00`

/** A free hour's time in the editor's address ("06:00"). */
export const calendarSlotTime = (hour: number) => `${String(hour).padStart(2, '0')}:00`

/**
 * The bar's label in the UI language: the week's month, or its two months, and the year its Sunday falls in
 * ("October 2026", "December – January 2027"). Runs in renders, so it never throws.
 */
export const formatCalendarMonths = (monday: string, language: string) => {
  const from = new Date(`${monday}T00:00:00Z`)
  if (Number.isNaN(from.getTime())) return { months: monday, year: '' }
  const to = new Date(`${addDays(monday, 6)}T00:00:00Z`)
  const month = new Intl.DateTimeFormat(dayLocaleOf(language), { month: 'long', timeZone: 'UTC' })
  const months = from.getUTCMonth() === to.getUTCMonth() ? month.format(from) : `${month.format(from)} – ${month.format(to)}`
  return { months, year: String(to.getUTCFullYear()) }
}
